import { useTranslation } from "react-i18next";
import { Button } from "../../components/button";
import { GenericForm } from "../../components/generic-form";
import { DocumentDataUpdateProps } from "./Document.types";
import AutocompleteOpt from "../../components/autocomplete-opt/AutocompleteOpt";
import { FileUpload } from "../../modules/file-upload";
import { useUploadDocument } from "../upload-document/useUploadDocument";
import { SplitPdfModal } from "../upload-document/SplitPdfModal";
import { useState } from "react";
import { DocumentTags } from "../../components/document-tags";
import { EmailTagInput } from "../../components/email-tag-input";

export function DocumentDataUpdate({
  docType,
  metaDataForm,
  onUpdate,
  updating = false,
  labels,
  onLabelChange,
  onFileReplace,
  tags = [],
  availableTags = [],
  onTagsChange,
  loadingTags = false,
  explicitAccessEmails = [],
  onExplicitAccessEmailsChange,
}: DocumentDataUpdateProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);

  const { showSplitPdfModal, onDontSplitPdf, onSplitPdf, dismissFileUpload, onUpload } =
    useUploadDocument({
      editMode: true,
      onUploadFinal: (finalFile) => {
        setSelectedFile(finalFile);
        onFileReplace?.(finalFile);
      },
    });

  return (
    <div className="d-flex flex-column p-3 rounded-2" style={{ backgroundColor: "#fff" }}>
      <div className="form-group">
        <label>{t("NEW_DOCUMENT.DOCUMENT_TYPE")}</label>
        <input className="form-control bg-light cursor-text" value={docType?.name ?? ""} disabled />
      </div>
      <AutocompleteOpt
        label={t("LABELS.LABEL")}
        placeholder={t("LABELS.SEARCH_LABEL")}
        onSelectOption={(labels) => onLabelChange(labels || ([] as any))}
        endpoint={"/labels/search-autocomplete"}
        isMultiple
        defaultValue={labels.map((label) => label.value)}
      />

      <div className="mt-4">
        <h5>{t("DOCUMENT_EXPLICIT_ACCESS.LABEL")}</h5>
        <EmailTagInput
          value={explicitAccessEmails}
          onChange={onExplicitAccessEmailsChange || (() => {})}
          placeholder={t("DOCUMENT_EXPLICIT_ACCESS.PLACEHOLDER")}
        />
      </div>

      <DocumentTags
        availableTags={availableTags}
        selectedTags={tags}
        onTagsChange={onTagsChange || (() => {})}
        loading={loadingTags}
      />

      <div className="mt-4">
        <h5>{t("DOCUMENTS.UPLOAD")}</h5>
        <FileUpload maxFiles={1} onUpload={onUpload} accept={{ "application/pdf": [] }} />

        {selectedFile && (
          <div
            className="mt-2 d-flex justify-content-center align-items-center bg-light p-2 rounded mx-auto"
            style={{ maxWidth: "500px" }}
          >
            <div className="text-truncate">
              <strong>{selectedFile.name}</strong>
              <small className="text-muted ms-2">
                ({(selectedFile.size / 1024).toFixed(1)} KB)
              </small>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        {metaDataForm ? (
          <div>
            <h5>{t("NEW_DOCUMENT.DATA")}</h5>
            <GenericForm
              className="mt-3"
              form={metaDataForm}
              onSubmit={onUpdate}
              submitControl={(submit) => (
                <Button
                  className="btn btn-primary w-100 mt-3"
                  type="button"
                  onClick={submit}
                  isLoading={updating}
                  disabled={updating}
                >
                  {t("SAVE_BUTTON")}
                </Button>
              )}
            />
          </div>
        ) : null}
      </div>

      <SplitPdfModal
        show={showSplitPdfModal}
        onSelectSplitFile={onSplitPdf}
        onSelectDontSplitFile={onDontSplitPdf}
        onDismissUpload={dismissFileUpload}
      />
    </div>
  );
}
